import { API_BASE_URL } from '../config.js';

/**
 * Normalized API error. Carries the HTTP status and any field-level violations
 * the backend reported (422 responses), so a form can point at the field that
 * was rejected instead of only showing the general message.
 *
 * The backend sends `violations` as a flat array of **message strings** (see
 * `VerifyController` in `../lang`), and in that case its `error` field is only a
 * generic "Validation failed." — so the violations carry the whole explanation
 * and must be shown, not just the message.
 *
 * `retryAfter` is the other field a form has to show: on a `429` the backend says
 * how many **seconds** are left before the next attempt is allowed
 * (`retry_after`), and only `/register` is rate limited (one code per address per
 * 5 minutes, plus a looser per-IP quota). `null` on every other status.
 *
 * @param {string} message Human-readable message (Polish, from the backend).
 * @param {{ status?: number, violations?: Array<string>, retryAfter?: ?number }} [options]
 *   - status: HTTP status; `0` for a network failure.
 *   - violations: as sent by the backend; normalized to an array.
 *   - retryAfter: whole seconds until the next attempt, or `null`.
 */
export class ApiError extends Error {
  constructor(message, { status = 0, violations = [], retryAfter = null } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.violations = Array.isArray(violations) ? violations : [];
    this.retryAfter = retryAfter;
  }
}

/**
 * Whole seconds from a `retry_after` value, or `null` when there is nothing
 * usable. Anything that isn't a positive finite number (an HTTP-date in
 * `Retry-After`, a `0`, a missing field) counts as "no countdown to show".
 */
function parseRetryAfter(value) {
  const seconds = Math.ceil(Number(value));
  return Number.isFinite(seconds) && seconds > 0 ? seconds : null;
}

/**
 * Perform a JSON request and normalize failures into an ApiError.
 * @param {string} method HTTP method.
 * @param {string} path   Path appended to API_BASE_URL.
 * @param {{ body?: object, jwt?: string }} [options]
 */
async function request(method, path, { body, jwt } = {}) {
  const headers = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (jwt) headers.Authorization = `Bearer ${jwt}`;

  let response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    // Network failure / CORS / server down.
    throw new ApiError('Brak połączenia z serwerem. Sprawdź sieć i spróbuj ponownie.', {
      status: 0,
    });
  }

  let data = null;
  try {
    data = await response.json();
  } catch {
    /* response had no / invalid JSON body */
  }

  if (!response.ok) {
    const message =
      (data && typeof data.error === 'string' && data.error) ||
      `Wystąpił błąd serwera (HTTP ${response.status}).`;
    throw new ApiError(message, {
      status: response.status,
      violations: data && data.violations,
      // The body is the source here: the API sends the same number in a
      // `Retry-After` header, but it is not in `Access-Control-Expose-Headers`,
      // so a cross-origin response (i.e. every response, see CLAUDE.md) hides it
      // from JS. The header read is only a same-origin fallback.
      retryAfter: parseRetryAfter(data?.retry_after ?? response.headers.get('Retry-After')),
    });
  }

  return data;
}

const postJson = (path, body) => request('POST', path, { body });
const getJson = (path, jwt) => request('GET', path, { jwt });

/**
 * Step 1 — request a verification code.
 * @returns {Promise<{jwt: string, token_type: string, expires_in: number}>}
 */
export function register(email) {
  return postJson('/api/v1/user/register', { email });
}

/**
 * Step 2 — exchange the registration token + code for a long-lived access token.
 * The response also carries the verified `email`, which the UI persists and
 * displays while signed in.
 * @returns {Promise<{jwt: string, email: string, token_type: string, expires_in: number}>}
 */
export function verify(jwt, code) {
  return postJson('/api/v1/user/verify', { jwt, code });
}

/**
 * Permanently delete the signed-in user's account. Frontend contract for the
 * "Usuń konto" action — authenticated with the access token.
 * @param {string} jwt Access token.
 */
export function deleteAccount(jwt) {
  return request('DELETE', '/api/v1/user', { jwt });
}

/**
 * Bootstrap payload the app requests on every launch: global app metadata plus
 * the ordered list of study chapters. Authenticated with the access token.
 *
 * The caller's currently-installed version (if any) is sent as the `ver` query
 * parameter and the runtime platform as `platform`, so the server knows which
 * version the user is running and whether they're on iOS / Android — and can,
 * in turn, surface an `alert` / update prompt.
 *
 * `softUpdate` is part of the response but deliberately not consumed anywhere.
 * The two flags that *are* acted on both land in `views/home.js`: `hardUpdate`
 * gates the app (`renderHardUpdate`, and the block is remembered in localStorage so
 * it also holds offline), and `hardLogout` — a plain boolean — signs the user out
 * with a backup on the spot (`signOutByServerOrder`), remembering nothing, since it
 * is a one-shot order rather than a state.
 *
 * @param {string} jwt Access token.
 * @param {{ version?: ?string, platform?: ?string }} [params]
 *   `version` last stored from a previous response; `platform` e.g. `ios`/`android`.
 * @returns {Promise<{
 *   name: string,
 *   version: string,
 *   softUpdate: ?object,
 *   hardUpdate: ?{title: string, description: string, link: string, linkText: ?string},
 *   hardLogout: ?boolean,
 *   header: ?string,
 *   alert: ?string,
 *   info: ?string,
 *   chapters: Array<{id: string, title: string, description: ?string, label: ?string, image: ?string, version: string, position: number}>,
 *   coming_soon: ?Array<{id: string, title: string, description: ?string, position: number}>
 * }>}
 */
export function fetchStudyMain(jwt, { version, platform } = {}) {
  const query = new URLSearchParams();
  if (version) query.set('ver', version);
  if (platform) query.set('platform', platform);
  const qs = query.toString();
  return getJson(`/api/v1/study/main${qs ? `?${qs}` : ''}`, jwt);
}

/**
 * Detail payload for a single chapter: the chapter's own fields plus its ordered
 * list of topics. Fetched on demand when a chapter is opened (and its topics are
 * not yet cached, or the chapter is flagged stale). Authenticated with the
 * access token.
 * @param {string} jwt Access token.
 * @param {string} chapterId Chapter id (from the `/study/main` listing).
 * @returns {Promise<{id: string, title: string, description: ?string, version: string, topics: Array<{id: string, title: string, intro: ?string, image: ?string, version: string, position: number}>}>}
 */
export function fetchStudyChapter(jwt, chapterId) {
  return getJson(`/api/v1/study/chapter/${encodeURIComponent(chapterId)}`, jwt);
}

/**
 * Full content for a single topic: its `labels`, the `audio` map
 * (`sentenceId → mp3 url`, dialogue sentences only) and the per-level `levels`,
 * each carrying `dialogues`, `phrases` and `exercises`. Fetched on demand when a
 * topic is opened and its content is not yet cached (or its head is flagged
 * stale). Authenticated with the access token.
 *
 * Mind the shapes, they differ per field: `phrases` is a **map** of
 * `phraseId → object` (not of strings, and each phrase carries its own audio
 * path), while `exercises` is an **array** whose `content` is the Polish prompt
 * and `translation` the English answer — the reverse of a dialogue sentence.
 *
 * @param {string} jwt Access token.
 * @param {string} topicId Topic id (from a `topics_head` row).
 * @returns {Promise<{
 *   id: string,
 *   labels: Array<string>,
 *   audio: Record<string, string>,
 *   levels: Array<{
 *     id: string,
 *     name: string,
 *     position: number,
 *     dialogues: Array<{id: string, person: string, position: number, sentences: Array<{id: string, sentence: string, translation: string, tense: ?string}>}>,
 *     phrases: Record<string, {id: string, content: string, translation: ?string, description: ?string, audio: ?string, examples: ?Array<{sentence: string, translation: ?string}>}>,
 *     exercises: Array<{id: string, content: string, translation: string}>
 *   }>
 * }>}
 */
export function fetchStudyTopic(jwt, topicId) {
  return getJson(`/api/v1/study/topic/${encodeURIComponent(topicId)}`, jwt);
}

/**
 * The account's stored backup of its **own** data — the one `/study` endpoint pair
 * that carries user state instead of course content (see `study/userData.js` for
 * what goes in it and why).
 *
 * The server stores the document verbatim and never looks inside, so the format is
 * entirely ours; the account is whoever the bearer token says it is, which is why
 * both calls need a live access token.
 *
 * Answers `null` when there is nothing to restore — that covers both a `204` (this
 * account has never stored a backup) and a stored `null`; the two are the same
 * thing to us. Every other failure arrives as an `ApiError`.
 *
 * @param {string} jwt Access token.
 * @returns {Promise<?object>}
 */
export function fetchStudyBackup(jwt) {
  return getJson('/api/v1/study/backup', jwt);
}

/**
 * Store `payload` as the account's backup, replacing whatever was there — there is
 * one row per account and no history.
 *
 * Resolves with `null` (the endpoint answers `204`, holding nothing worth sending
 * back). Beyond the usual failures this one can answer **413** when the document
 * exceeds the server's `app.backup_max_bytes`; like every other status it surfaces
 * as an `ApiError` with the backend's Polish message.
 *
 * @param {string} jwt Access token.
 * @param {object} payload Any JSON-serializable document.
 */
export function saveStudyBackup(jwt, payload) {
  return request('POST', '/api/v1/study/backup', { body: payload, jwt });
}
